import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { CommonModule, HashLocationStrategy, LocationStrategy } from "@angular/common";
import { ToastrModule } from "ngx-toastr";
import { NgxPaginationModule } from "ngx-pagination";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { CoreModule } from "./core/core.module";
import { HomeComponent } from "./features/home/home.component";
import { LoginComponent } from "./features/login/login.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgxSpinnerModule } from "ngx-spinner";
import { AngularDualListBoxModule } from "angular-dual-listbox";
import {
  MatCheckboxModule,
  MatFormFieldModule,
  MatIconModule,
  MatInputModule,
  MatTreeModule,
  MatMenuModule,
  MatButtonModule,
} from "@angular/material";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { SwiperModule } from "ngx-swiper-wrapper";
import { NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { ChartsModule } from "ng2-charts";

import { ConfigWeatherComponent } from "./features/config-weather/config-weather.component";
import { CreateFarmComponent } from "./features/create-farm/create-farm.component";
import { PaginationModule } from "ngx-bootstrap/pagination";
import { FarmDetailComponent } from "./features/farm-detail/farm-detail.component";
import { FarmHistoryComponent } from "./features/farm-history/farm-history.component";
import { ConfigEmoticonComponent } from "./features/config-emoticon/config-emoticon.component";
import { CreateDeviceComponent } from "./features/create-device/create-device.component";
import { PlotComponent } from "./features/plot/plot.component";
import { CreatePlotComponent } from "./features/create-plot/create-plot.component";

import { ConfigFarmComponent } from "./features/config-farm/config-farm.component";
import { ConfigPlotComponent } from "./features/config-plot/config-plot.component";
import { ConfigHistoryComponent } from "./features/config-history/config-history.component";
import { ConfigSensorComponent } from "./features/config-sensor/config-sensor.component";

import { SchedualDetailComponent } from "./features/schedual-detail/schedual-detail.component";
import { CreateSchedualComponent } from "./features/schedual-detail/create-schedual/create-schedual.component";
import { EditScheduleComponent } from "./features/schedual-detail/edit-schedual/edit-schedual.component";
import { SchedualDetailCheckConfigComponent } from "./features/schedual-detail/schedual-detail-check-config/schedual-detail-check-config.component";
import { SchedualDetailSyncConfigComponent } from "./features/schedual-detail/schedual-detail-sync-config/schedual-detail-sync-config.component";

import { ManageThingComponent } from "./features/manage-thing/manage-thing.component";
import { AgreementRegisterComponent } from "./features/agreement-register/agreement-register.component";
import { NgxGaugeModule } from 'ngx-gauge';
import { BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { TimepickerModule } from "ngx-bootstrap/timepicker";
import { ManageUserComponent } from "./features/manage-user/manage-user.component";
import { CreateUserComponent } from "./features/create-user/create-user.component";
import { DrawFarmComponent } from "./features/draw-farm/draw-farm.component";
import { LeafletModule } from "@asymmetrik/ngx-leaflet";
import { FarmDashboardComponent } from "./features/farm-dashboard/farm-dashboard.component";
import { TokenInterceptor } from "./core/token.interceptor";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { PlotYieldComponent } from "./features/plot-yield/plot-yield.component";
import { PlotDetailComponent } from "./features/plot-detail/plot-detail.component";
import { PlotGrowthComponent } from "./features/plot-growth/plot-growth.component";
import { PlotEventComponent } from "./features/plot-event/plot-event.component";
import { PlotExperimentComponent } from "./features/plot-experiment/plot-experiment.component";
import { NgCircleProgressModule } from "ng-circle-progress";
import { CreateCropComponent } from "./features/create-crop/create-crop.component";
import { PreloadComponent } from "./features/preload/preload.component";
import { LeafletMapComponent } from "./shared/layout/leaflet-map/leaflet-map.component";
import { MatTableModule } from "@angular/material/table";
import { FullCalendarModule } from "@fullcalendar/angular"; // the main connector. must go first
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction"; // a plugin
import { Ng2SearchPipeModule } from "ng2-search-filter";
import { HighchartsChartModule } from "highcharts-angular";
import { MatSortModule } from "@angular/material/sort";
import { MaterialExampleModule } from "./material.module";

FullCalendarModule.registerPlugins([
  // register FullCalendar plugins
  dayGridPlugin,
  timeGridPlugin,
  listPlugin,
  interactionPlugin,
]);
import { AgmCoreModule, GoogleMapsAPIWrapper, PolygonManager } from "@agm/core";
import { AgmDrawingModule } from "@agm/drawing";

import { NgxImageCompressService } from "ngx-image-compress";
import { ReportComponent } from "./features/report/report.component";
import { PreloadPlotDetailComponent } from "./features/preload-plot-detail/preload-plot-detail.component";
import { ConfigColorComponent } from "./features/config-color/config-color.component";
import { DateRangePickerDirective } from "./shared/utils/directives/date-range-picker.directive";
import { NgbdDropdownBasic } from "./shared/layout/dropdown-basic/dropdown-basic";
import { HighchartComponent } from "./features/graph/highchart/highchart.component";
import { GraphComponent } from "./features/graph/graph/graph.component";
import { OverviewGraphComponent } from "./features/graph/overview-graph/overview-graph.component";
import { GraphHistoryComponent } from "./features/graph-history/graph-history/graph-history.component";
import { OverviewGraphHistoryComponent } from "./features/graph-history/overview-graph-history/overview-graph-history.component";
import { HighchartHistoryComponent } from "./features/graph-history/highchart-history/highchart-history.component";
import { GraphHistoryFilterComponent } from "./features/graph-history/graph-history-filter/graph-history-filter.component";
import { UtilsModule } from "./shared/utils/utils.module";
import { ReportFlowerYearComponent } from "./features/report/report-flower-year/report-flower-year.component";
import { ReportFlowerPoltComponent } from "./features/report/report-flower-polt/report-flower-polt.component";
import { ReportOverviewComponent } from "./features/report/report-overview/report-overview.component";
import { ReportFlowerZoneComponent } from "./features/report/report-flower-zone/report-flower-zone.component";
import { ReportPodYearComponent } from "./features/report/report-pod-year/report-pod-year.component";
import { ReportPodZoneComponent } from "./features/report/report-pod-zone/report-pod-zone.component";
import { ReportPodPlotComponent } from "./features/report/report-pod-plot/report-pod-plot.component";
import { ManageYieldListComponent } from "./features/manage-yield/manage-yield-list/manage-yield-list.component";
import { ManageYieldViewComponent } from "./features/manage-yield/manage-yield-view/manage-yield-view.component";
import { ManageYieldEditComponent } from "./features/manage-yield/manage-yield-edit/manage-yield-edit.component";
import { ManageFarmListComponent } from "./features/manage-farm/manage-farm-list/manage-farm-list.component";
import { ManageFarmCreateComponent } from "./features/manage-farm/manage-farm-create/manage-farm-create.component";
import { ManageFarmViewComponent } from "./features/manage-farm/manage-farm-view/manage-farm-view.component";
import { ManageFarmEditComponent } from "./features/manage-farm/manage-farm-edit/manage-farm-edit.component";
import { ManageFarmPlotCreateComponent } from "./features/manage-farm/manage-farm-plot-create/manage-farm-plot-create.component";
import { ManageFarmPlotEditComponent } from "./features/manage-farm/manage-farm-plot-edit/manage-farm-plot-edit.component";
import { YieldReportComponent } from "./features/yield-report/yield-report.component";
import { YieldReportSubComponent } from "./features/yield-report-sub/yield-report-sub.component";
import { environment } from "src/environments/environment";
import { ManageThingListComponent } from "./features/manage-thing/manage-thing-list/manage-thing-list.component";
import { ManageThingViewComponent } from "./features/manage-thing/manage-thing-view/manage-thing-view.component";
import { ManageThingUpdateComponent } from "./features/manage-thing/manage-thing-update/manage-thing-update.component";
import { ManageThingSensorEditComponent } from "./features/manage-thing/manage-thing-sensor-edit/manage-thing-sensor-edit.component";
import { ManageThingFilterComponent } from "./features/manage-thing/manage-thing-filter/manage-thing-filter.component";
import { ManageThingSensorCreateComponent } from "./features/manage-thing/manage-thing-sensor-create/manage-thing-sensor-create.component";
import { ManageThingMapComponent } from "./features/manage-thing/manage-thing-map/manage-thing-map.component";
import { ManageThingProcessComponent } from "./features/manage-thing/manage-thing-process/manage-thing-process.component";
import { CalendarImageListComponent } from "./features/calendar-image/calendar-image-list/calendar-image-list.component";
import { CalendarImageSlideComponent } from "./features/calendar-image/calendar-image-slide/calendar-image-slide.component";
import { ThaiDatehPipe } from "./shared/utils/pipes/thai-date.pipe";
import { WeatherForecastComponent } from "./features/weather-forecast/weather-forecast/weather-forecast.component";

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoginComponent,
    ConfigWeatherComponent,
    CreateFarmComponent,
    FarmDetailComponent,
    FarmHistoryComponent,
    ConfigEmoticonComponent,
    CreateDeviceComponent,
    SchedualDetailComponent,
    CreateSchedualComponent,
    EditScheduleComponent,
    SchedualDetailCheckConfigComponent,
    SchedualDetailSyncConfigComponent,

    PlotComponent,
    CreatePlotComponent,
    ConfigFarmComponent,
    ConfigPlotComponent,
    ConfigHistoryComponent,
    ConfigSensorComponent,
    ManageThingComponent,
    AgreementRegisterComponent,
    ManageUserComponent,
    CreateUserComponent,
    DrawFarmComponent,
    FarmDashboardComponent,
    PlotYieldComponent,
    PlotDetailComponent,
    PlotGrowthComponent,
    PlotEventComponent,
    PlotExperimentComponent,
    CreateCropComponent,
    PreloadComponent,
    LeafletMapComponent,
    ReportComponent,
    PreloadPlotDetailComponent,
    ConfigColorComponent,

    GraphComponent,
    ThaiDatehPipe,
    DateRangePickerDirective,
    NgbdDropdownBasic,
    HighchartComponent,
    OverviewGraphComponent,
    GraphHistoryComponent,
    OverviewGraphHistoryComponent,
    HighchartHistoryComponent,
    GraphHistoryFilterComponent,
    ReportFlowerYearComponent,
    ReportFlowerPoltComponent,
    ReportOverviewComponent,
    ReportFlowerZoneComponent,
    ReportPodYearComponent,
    ReportPodZoneComponent,
    ReportPodPlotComponent,
    ManageYieldListComponent,
    ManageYieldViewComponent,
    ManageYieldEditComponent,

    ManageFarmListComponent,
    ManageFarmCreateComponent,
    ManageFarmViewComponent,
    ManageFarmEditComponent,

    ManageFarmPlotCreateComponent,
    ManageFarmPlotEditComponent,

    YieldReportComponent,
    YieldReportSubComponent,

    ManageThingListComponent,
    ManageThingViewComponent,
    ManageThingUpdateComponent,
    ManageThingSensorCreateComponent,
    ManageThingSensorEditComponent,
    ManageThingFilterComponent,
    ManageThingMapComponent,
    ManageThingProcessComponent,

    CalendarImageListComponent,
    CalendarImageSlideComponent,
    WeatherForecastComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    NgxGaugeModule,
    AppRoutingModule,
    CoreModule,
    NgxSpinnerModule,
    AngularDualListBoxModule,
    ToastrModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot(),
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatMenuModule,
    BrowserAnimationsModule,
    MatIconModule,
    MatInputModule,
    MatTreeModule,
    PaginationModule.forRoot(),
    FormsModule,
    LeafletModule,
    NgMultiSelectDropDownModule.forRoot(),
    ReactiveFormsModule,
    SwiperModule,
    MatSortModule,
    MaterialExampleModule,
    // DataTablesModule,
    ChartsModule,
    NgxPaginationModule,
    TooltipModule,
    HttpClientModule,
    MatTableModule,
    NgCircleProgressModule.forRoot({}),
    FullCalendarModule, // register FullCalendar with you app
    Ng2SearchPipeModule,
    HighchartsChartModule,
    UtilsModule,
    AgmCoreModule.forRoot({
      apiKey: environment.GOOGLE_KEY,
      // DrawingManager was removed in Maps JS 3.65 (quarterly as of Aug 2026).
      // Pin to 3.64 until plot drawing is migrated off @agm/drawing.
      apiVersion: "3.64",
      libraries: ["drawing", "places", "geometry"],
    }),
    AgmDrawingModule,
    DragDropModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
      // useValue: DEFAULT_SWIPER_CONFIG
    },
    NgxImageCompressService,
    GoogleMapsAPIWrapper,
    PolygonManager,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
